/**
 * Your profile (Phase 4): the games, the streaks, the daily-game history and
 * your rarest moves — see `profile-data.js` for how each is worked out.
 *
 * Signed in, the progress part is your account's, merged across devices
 * (`progress.js`); your games and their reviews are always this browser's,
 * because they never leave it. Signed out, it is all this browser's, and the
 * page says so.
 */

import { mountNav } from '../nav.mjs';
import { accountUser, playerUrl } from '../account-link.js';
import { onMerged, read as readProgress, syncSoon } from '../progress.js';
import { DAILY_KEY, dayNumber } from '../daily/day.js';
import { dailyStats, practiceStats } from './profile-data.js';
import { loadYours } from './yours.js';
import { formatLevel } from '../level.js';

const $ = (id) => document.getElementById(id);
mountNav($('nav-slot'), { base: '../' });

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const pct = (x) => `${Math.round(x * 100)}%`;
const SOURCE_WORDS = { lichess: 'Lichess', chesscom: 'Chess.com', pgn: 'PGN files', engine: 'the engine page', shared: 'shared links' };

const today = dayNumber();

function tile(label, value, hint = '') {
  return `<div class="pf-tile"><span class="pf-tile-label">${esc(label)}</span>`
    + `<span class="pf-tile-value">${esc(value)}</span>${hint ? `<span class="pf-tile-hint">${esc(hint)}</span>` : ''}</div>`;
}

/**
 * The level and the score, from the server — the numbers on this page the
 * browser does not work out, because they are the ones other people see. See
 * `worker/players.js`.
 */
async function drawScore(user) {
  if (!user?.username) return;
  try {
    const response = await fetch(new URL(`../api/players/u/${encodeURIComponent(user.username)}`, import.meta.url), { credentials: 'same-origin' });
    if (!response.ok) return;
    const { player: p } = await response.json();
    const bits = [`Level <b>${formatLevel(p.level?.level)}</b>`, `score <b>${p.total.toLocaleString()}</b>`];
    if (p.rank) bits.push(`#${p.rank.toLocaleString()} on the <a href="../leaderboard/">leaderboard</a>`);
    $('pf-sub').insertAdjacentHTML('beforeend', `<br>${bits.join(' · ')}. <a href="${esc(playerUrl(user.username))}">Your public page</a>`
      + (p.public ? '' : ' (private — <a href="../account/">make it public</a>)'));
  } catch { /* offline: the rest of the page stands */ }
}

function drawHead(user) {
  const name = user ? (user.username || user.lichess || user.email) : '';
  $('pf-name').textContent = name || 'You';
  $('pf-badge').textContent = (name.match(/[a-z0-9]/i)?.[0] || '?').toUpperCase();
  $('pf-sub').innerHTML = user
    ? `Since ${esc(new Date(user.created).toLocaleDateString(undefined, { year: 'numeric', month: 'long' }))}. Your progress follows your account; your games stay in this browser.`
    : 'Everything here is from this browser. <a href="../account/">Sign in</a> and your progress follows you to your other devices.';
}

function drawProgress() {
  const daily = dailyStats(readProgress(DAILY_KEY), today);
  const practice = practiceStats(readProgress);
  $('pf-tiles').dataset.progress = '1';
  const tiles = [
    tile('Daily streak', daily.streak ? `${daily.streak} ${daily.streak === 1 ? 'day' : 'days'}` : '–', daily.best ? `longest ${daily.best}` : 'play today to start one'),
    tile('Mates found', practice.mates.toLocaleString(), 'in the famous games'),
    tile('Games guessed', practice.guessedGames.toLocaleString(), practice.guessedShare == null ? 'move by move' : `${pct(practice.guessedShare)} of moves found`),
    tile('Your mistakes, learned', practice.mistakes.learned.toLocaleString(), practice.mistakes.tried ? `of ${practice.mistakes.tried} tried` : 'none tried yet'),
    tile('Lessons done', practice.lessons.toLocaleString(), 'how the pieces move'),
  ];
  $('pf-tiles').innerHTML = tiles.join('') + ($('pf-tiles').dataset.games || '');

  $('pf-daily-say').textContent = daily.played
    ? `${plural(daily.played, 'day')} played, ${daily.won} solved (${pct(daily.won / daily.played)}).`
    : 'Not played yet. A famous game, five moves at a time, new every day.';
  $('pf-days').setAttribute('aria-label', `The last ${daily.grid.length} days: ${daily.won} won`);
  $('pf-days').innerHTML = daily.grid
    .map((d) => `<span class="pf-day ${d.state}${d.day === today ? ' today' : ''}" title="Daily #${d.day}: ${d.state === 'none' ? 'not played' : d.state === 'won' ? 'solved' : 'not solved'}"></span>`)
    .join('');
}

async function drawGames() {
  const { stats, finds, reviewed } = await loadYours();
  const sources = Object.entries(stats.sources).sort((a, b) => b[1] - a[1])
    .map(([s, n]) => `${n.toLocaleString()} from ${SOURCE_WORDS[s] || s}`);
  $('pf-games-say').innerHTML = stats.total
    ? `${esc(plural(stats.total, 'game'))} in this browser: ${esc(sources.join(', '))}.`
      + (stats.yours < stats.total ? ` ${esc(plural(stats.yours, 'is', 'are'))} clearly yours.` : '')
    : 'No games here yet. <a href="../#import">Import yours</a> from Lichess or Chess.com.';
  // The games played ON this site — the engine, friends, Lichess through our
  // board — are their own list, finished and unfinished: `../my-games/`.
  $('pf-games-say').insertAdjacentHTML('beforeend',
    ' Games you played here are in <a href="../my-games/">Your games</a>, to review or carry on.');

  const { won, drawn, lost } = stats.results;
  const decided = won + drawn + lost;
  $('pf-results').hidden = !decided;
  if (decided) {
    const seg = (n, cls, word) => (n ? `<span class="pf-seg ${cls}" style="flex:${n}" title="${n} ${word}">${n} ${word}</span>` : '');
    $('pf-results').innerHTML = seg(won, 'won', 'won') + seg(drawn, 'drawn', 'drawn') + seg(lost, 'lost', 'lost');
  }
  $('pf-openings').hidden = !stats.openings.length;
  $('pf-openings').querySelector('tbody').innerHTML = stats.openings.map((o) => `<tr><td>${esc(o.name)}</td>`
    + `<td>${o.side === 'w' ? 'White' : 'Black'}</td><td class="n">${o.games}</td><td class="n">${pct(o.points / o.games)}</td></tr>`).join('');

  $('pf-legends-say').innerHTML = stats.legends.length
    ? 'The games you kept from the engine page.'
    : 'Play the legends on the <a href="../play/vs-computer.html">engine page</a> and keep the game: it is counted here.';
  $('pf-legends-list').innerHTML = stats.legends.map((l) => `<li><span class="pf-li-main">${esc(l.name)}</span>`
    + `<span class="pf-li-side">${l.won ? `beaten ${l.won} of ${l.games}` : `${plural(l.games, 'game')}, not beaten yet`}</span></li>`).join('');

  $('pf-tiles').dataset.games = tile('Your games', stats.total.toLocaleString(), decided ? `${won} won, ${drawn} drawn, ${lost} lost` : 'in this browser');
  drawProgress();

  // The rarest moves, from reviewed games where your side is known (`yours.js`).
  $('pf-finds-say').textContent = finds.length
    ? 'Moves where only one move held the position, and you found it — the hardest first.'
    : reviewed
      ? 'None yet: in your reviewed games, no position had only one good move that you found. Keep reviewing.'
      : 'Review your games (open one from the library) and the hardest moves you found appear here.';
  $('pf-finds-list').innerHTML = finds.map((f) => `<li><a href="../watch/player.html?game=${encodeURIComponent(f.gameId)}&ply=${f.k}">`
    + `<span class="pf-li-main"><b>${f.moveNo}${f.side === 'w' ? '.' : '…'}${esc(f.san)}</b> in ${esc(f.title)}</span>`
    + `<span class="pf-li-side">${esc(f.text)}</span></a></li>`).join('');
}

// Signed in with a username, your profile IS your page (`/@/<name>`), which
// shows all of this to you and what you choose of it to everybody else. This
// page stays for everybody else: somebody signed out still has a browser full
// of games and streaks to look at.
accountUser.then((user) => {
  if (user?.username) { location.replace(playerUrl(user.username)); return; }
  drawHead(user);
  drawScore(user);
});
drawHead(null);
drawProgress();
drawGames();
onMerged(drawProgress);
syncSoon();
